import { LitElement, html, css } from 'lit'
import { api } from '../api.js'
import { toast } from './app-toast.js'

// Workamajig company/job/task picker modal (§18.3, §19.1). Shown imperatively by
// wmj-flow when an agent's first ticket action is blocked by WMJ_CLIENT_REQUIRED /
// WMJ_JOB_REQUIRED / WMJ_TASK_REQUIRED. Fetches the list, filters client-side, and
// emits `pick` with the chosen record (or `cancel`). It does NOT perform the PATCH —
// the flow orchestrates that.
class WmjPicker extends LitElement {
  static properties = {
    mode: { type: String },      // 'company' | 'job' | 'task'
    clientId: { type: String },  // Workamajig company key, to scope job lookups
    jobNumber: { type: String }, // Workamajig job number, to scope task lookups
    _items: { state: true },
    _query: { state: true },
    _loading: { state: true },
    _refreshing: { state: true },
    _error: { state: true }
  }

  static styles = css`
    :host { position: fixed; inset: 0; z-index: 1100; display: flex; align-items: center;
            justify-content: center; background: rgba(20, 20, 32, 0.5); }
    .card { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            box-shadow: var(--bx-shadow); width: min(480px, 92vw); max-height: 80vh; display: flex;
            flex-direction: column; overflow: hidden; }
    header { display: flex; align-items: baseline; justify-content: space-between; padding: 1rem 1.1rem 0.6rem; }
    h2 { margin: 0; font-size: 1.05rem; color: var(--bx-ink); }
    .sub { color: var(--bx-muted); font-size: 0.8rem; }
    .search { padding: 0 1.1rem 0.6rem; }
    input { width: 100%; box-sizing: border-box; font: inherit; padding: 0.45rem 0.6rem;
            border: 1px solid var(--bx-border); border-radius: var(--bx-radius); background: var(--bx-bg); color: var(--bx-ink); }
    .list { overflow-y: auto; border-top: 1px solid var(--bx-border); }
    .item { padding: 0.55rem 1.1rem; cursor: pointer; border-bottom: 1px solid var(--bx-border); }
    .item:hover { background: var(--bx-bg); }
    .item .name { color: var(--bx-ink); font-size: 0.9rem; }
    .item .meta { color: var(--bx-muted); font-size: 0.78rem; }
    .empty, .state { padding: 1.4rem 1.1rem; color: var(--bx-muted); font-size: 0.88rem; text-align: center; }
    footer { display: flex; gap: 0.5rem; justify-content: space-between; padding: 0.7rem 1.1rem; border-top: 1px solid var(--bx-border); }
    button { font: inherit; cursor: pointer; padding: 0.4rem 0.7rem; border: 1px solid var(--bx-border);
             border-radius: var(--bx-radius); background: var(--bx-surface); color: var(--bx-ink); font-size: 0.82rem; }
    button:disabled { opacity: 0.6; cursor: default; }
    /* A summary task is context, not a choice (§19.1). */
    .item.summary { cursor: default; opacity: 0.55; }
    .item.summary:hover { background: transparent; }
  `

  constructor() {
    super()
    this.mode = 'company'
    this.clientId = null
    this.jobNumber = null
    this._items = []
    this._query = ''
    this._loading = true
    this._refreshing = false
    this._error = null
  }

  connectedCallback() {
    super.connectedCallback()
    this._load()
    this._onKey = (e) => { if (e.key === 'Escape') this._cancel() }
    window.addEventListener('keydown', this._onKey)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('keydown', this._onKey)
  }

  get _isCompany() { return this.mode === 'company' }
  get _isTask() { return this.mode === 'task' }

  async _load() {
    this._loading = true
    this._error = null
    try {
      if (this._isCompany) {
        this._items = await api.get('/workamajig/clients')
      } else if (this._isTask) {
        // Only the tasks that can take time come back: the server drops the summary
        // roll-ups and the milestones, which Workamajig will not hold hours against
        // (§19.1). Nothing listed here is unpickable.
        this._items = await api.get(`/workamajig/tasks?jobNumber=${encodeURIComponent(this.jobNumber || '')}`)
      } else {
        this._items = await api.get(`/workamajig/jobs${this.clientId ? `?clientId=${encodeURIComponent(this.clientId)}` : ''}`)
      }
    } catch (err) {
      this._error = err.message || 'Failed to load Workamajig catalog'
    } finally {
      this._loading = false
    }
  }

  async _refresh() {
    this._refreshing = true
    try {
      await api.post('/workamajig/catalog/refresh')
      await this._load()
      toast('Catalog refreshed', 'success')
    } catch (err) {
      toast(err.message || 'Refresh failed', 'error')
    } finally {
      this._refreshing = false
    }
  }

  get _filtered() {
    const q = this._query.trim().toLowerCase()
    if (!q) return this._items
    return this._items.filter(i =>
      [i.name, i.jobNumber, i.clientName, i.clientId, i.taskId].some(v => String(v || '').toLowerCase().includes(q))
    )
  }

  _pick(item) {
    if (item.isSummary) return          // a roll-up, not a budget line
    this.dispatchEvent(new CustomEvent('pick', { detail: item }))
  }

  _title() {
    if (this._isCompany) return 'Link a Workamajig company'
    return this._isTask ? 'Choose the Workamajig task' : 'Assign a Workamajig job'
  }

  _sub() {
    if (this._isCompany) return 'This client is not yet linked to Workamajig. Choose its company.'
    return this._isTask
      ? 'Which task on this job does the ticket’s work belong to? Its hours are booked there.'
      : 'Choose an open job for this ticket. Closed and cancelled jobs aren’t listed.'
  }

  // What each row shows under its name: the company key, the job number, or — for a
  // task — whether it's a roll-up and who is already on it.
  _meta(item) {
    if (this._isCompany) return item.clientId
    if (this._isTask) {
      if (item.isSummary) return 'summary — not bookable'
      // Who is already on the task is what tells an agent it's the right line. The
      // task id is the WBS path ('3.1', '4.1.10.2') and the list is ordered by it, so
      // it also says where the task sits in the job's outline.
      const who = item.assignees || []
      const listed = who.length > 3 ? `${who.slice(0, 3).join(', ')} +${who.length - 3}` : who.join(', ')
      return `Task ${item.taskId} · ${who.length ? listed : 'nobody assigned yet'}`
    }
    return `#${item.jobNumber || item.jobId}${item.clientName ? ` · ${item.clientName}` : ''}`
  }

  // An empty list has two very different causes, and saying "No matches" to both is
  // how a picker becomes a dead end: the agent cannot tell a too-narrow search from a
  // list that was never going to have anything in it.
  _emptyText() {
    if (this._items.length > 0) return 'No matches for that search.'
    if (this._isCompany) return 'No Workamajig companies in the catalog. Try Refresh catalog.'
    if (this._isTask) {
      return 'This job has no tasks that can take time. A PM has to plan a budgeted task before a ticket can book hours to it.'
    }
    return 'No open jobs for this client. Closed and cancelled jobs can’t take new hours — ask a PM to open one.'
  }

  _cancel() {
    this.dispatchEvent(new CustomEvent('cancel'))
  }

  _onBackdrop(e) {
    if (e.target === this) this._cancel()
  }

  render() {
    return html`
      <div class="card" @click=${e => e.stopPropagation()}>
        <header>
          <h2>${this._title()}</h2>
        </header>
        <div class="sub" style="padding: 0 1.1rem 0.6rem;">${this._sub()}</div>
        <div class="search">
          <input placeholder=${this._isCompany ? 'Search companies…' : this._isTask ? 'Search tasks…' : 'Search jobs…'}
            .value=${this._query} @input=${e => (this._query = e.target.value)} autofocus />
        </div>
        <div class="list">
          ${this._loading ? html`<div class="state">Loading…</div>`
            : this._error ? html`<div class="state">${this._error}</div>`
            : this._filtered.length === 0 ? html`<div class="empty">${this._emptyText()}</div>`
            : this._filtered.map(item => html`
                <div class="item ${item.isSummary ? 'summary' : ''}" @click=${() => this._pick(item)}>
                  <div class="name">${item.name}</div>
                  <div class="meta">${this._meta(item)}</div>
                </div>`)}
        </div>
        <footer>
          <button ?disabled=${this._refreshing} @click=${this._refresh}>
            ${this._refreshing ? 'Refreshing…' : 'Refresh catalog'}
          </button>
          <button @click=${this._cancel}>Cancel</button>
        </footer>
      </div>
    `
  }

  createRenderRoot() {
    // Backdrop click-to-cancel needs the host element to receive the click.
    const root = super.createRenderRoot()
    this.addEventListener('click', e => this._onBackdrop(e))
    return root
  }
}

customElements.define('wmj-picker', WmjPicker)
